import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Alert, Autosuggest, Box, Button, FormField, Link, Modal, Select, SpaceBetween, Spinner,
} from '@amzn/awsui-components-react';
import { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import { AutosuggestProps } from '@amzn/awsui-components-react/polaris/autosuggest';
import CONSTANTS from 'src/utils/constants';
import {
    getCustomDataTableRowsSearchRequest,
    getCustomDataTablesSearchRequest,
} from 'src/components/custom-tables-shared/CustomDataTableSearchHelper';
import CustomDataTableRowsSearchRequest from 'src/models/custom-data-tables/CustomDataTableRowsSearchRequest';
import TPESearchRequest from 'src/models/common/TPESearchRequest';
import { CommonBalancePoolColumn } from 'src/models/multidimensional-allocation/CommonBalancePool';
import { CommonBalancePoolBuilderContext } from 'src/services/multidimensional-allocation/common-balance-pool/CommonBalancePoolBuilderContext';
import { ACTIONS } from 'src/services/multidimensional-allocation/common-balance-pool/CommonBalancePoolBuilderReducer';
import { sourceKeyOf } from './cbpInputs';

const INPUT_TYPE_OPTIONS: SelectProps.Option[] = [
    { label: 'Segmented CDT', value: 'SEGMENT_CDT' },
    { label: 'MDA allocation output', value: 'MDA_OUTPUT' },
];

/** Normalise a grain/segment string for comparison (strip case, spaces, underscores). */
const normSegment = (s?: string): string => (s ?? '').replace(/[^a-z0-9]/gi, '').toUpperCase();

/** The UI label for a CBP grain, e.g. COST_CENTER -> "Cost Center" (LEGAL_ENTITY maps to Company). */
const grainUiLabel = (grain: string): string =>
    (CONSTANTS.COA_SEGMENT_MAPPING as Record<string, { UI: string }>)[grain === 'LEGAL_ENTITY' ? 'COMPANY' : grain]?.UI ?? grain;

/** A segment CDT / MDA output is selectable only when its segment matches the pool grain. */
const segmentMatchesGrain = (grain: string, segment?: string): boolean => {
    if (!segment) return true; // no segment metadata -> don't gate (keeps the picker usable)
    const seg = normSegment(segment);
    return seg === normSegment(grain) || seg === normSegment(grainUiLabel(grain));
};

/** Sort a CDT legend's value keys (value1, value2, ...) numerically. */
const sortedLegendKeys = (legend: Record<string, string>): string[] =>
    Object.keys(legend).sort((a, b) => Number(a.replace('value', '')) - Number(b.replace('value', '')));

/**
 * Add-input dialog for the CBP builder Step 1. Consumes CommonBalancePoolBuilderContext (visibility,
 * edited row, grain, saving/error, services, persistColumns) rather than props. Two selections only —
 * the input type and the source object (a segment CDT table, or an MDA allocation output) — both at the
 * pool grain. Columns are derived from that source's definition; the merged column set is persisted via
 * the shared `persistColumns` (UpdateCommonBalancePool).
 */
export default function AddCbpInputModal() {
    const { cbpBuilderState, cbpBuilderDispatch, services, persistColumns } = useContext(CommonBalancePoolBuilderContext);
    const { addModalVisible: visible, editingRow, saving, saveError: error, pool } = cbpBuilderState;
    const grain = pool?.grain ?? '';
    const isEditing = !!editingRow;
    // Stable key for the input being edited so the open/seed + preselect effects don't re-run on every
    // parent re-render, which would otherwise reset the user's in-progress selection.
    const editingKey = editingRow ? (editingRow.cols?.[0]?.columnId ?? editingRow.sourceType ?? 'edit') : 'add';

    const [inputType, setInputType] = useState<SelectProps.Option>(INPUT_TYPE_OPTIONS[0]);
    // Text shown in the source Autosuggest (§12.2 filterable combobox). The resolved source is DERIVED
    // from this text below (via a display→source map) so we key off the stable id, not a collidable label.
    const [sourceText, setSourceText] = useState('');
    const [validationError, setValidationError] = useState<string | undefined>(undefined);

    const isCdt = inputType.value === 'SEGMENT_CDT';

    // Source lists. CDT tables + MDA sheets are fetched only while the modal is open (payload gated).
    const [tablesPayload, setTablesPayload] = useState<TPESearchRequest | undefined>(undefined);
    const [sheetsQuery, setSheetsQuery] = useState('');
    // The selected CDT table's rows are fetched so we can read its column legend on Add.
    const [rowsPayload, setRowsPayload] = useState<CustomDataTableRowsSearchRequest | undefined>(undefined);

    const [tablesResult, tablesLoading, tablesError] = services.customDataTablesService.searchCustomDataTables(tablesPayload);
    const [rowsResult, rowsLoading, rowsError] = services.customDataTablesService.searchCustomDataTableRows(
        rowsPayload as CustomDataTableRowsSearchRequest,
        [],
        false,
    );
    const [sheetsResult, sheetsLoading, sheetsError] = services.multidimensionalAllocationService.listMultiAllocationSheets(
        sheetsQuery,
        'ACTIVE',
    );
    // Surface a failed source-list / rows load so it's distinguishable from a genuinely empty result
    // (otherwise the Autosuggest just shows "none found" and buildColumns reports "no value columns").
    const sourceError = isCdt ? (tablesError || rowsError) : sheetsError;

    // Seed / reset each time the modal opens. Kick off the source fetch for the active input type.
    useEffect(() => {
        if (!visible) return;
        setValidationError(undefined);
        const type = editingRow?.sourceType ?? editingRow?.cols?.[0]?.sourceType ?? 'SEGMENT_CDT';
        setInputType(INPUT_TYPE_OPTIONS.find((o) => o.value === type) ?? INPUT_TYPE_OPTIONS[0]);
        setSourceText('');
        setTablesPayload(getCustomDataTablesSearchRequest(true));
        setSheetsQuery(`cbp-input-${Date.now()}`);
        setRowsPayload(undefined);
        // Seed only on open (and when the edited input changes) — keyed on a stable primitive.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, editingKey]);

    // Options for the "source object" dropdown, grain-gated (mismatched entries disabled, per the mockup).
    const cdtOptions: SelectProps.Option[] = useMemo(
        () =>
            (tablesResult?.tables ?? []).map((t) => {
                const selectable = segmentMatchesGrain(grain, t.coaSegment);
                return {
                    label: t.coaSegment ? `${t.tableName} (${t.coaSegment})` : t.tableName,
                    value: t.tableId,
                    description: t.tableName,
                    disabled: !selectable,
                    labelTag: selectable ? undefined : 'not selectable',
                };
            }),
        [tablesResult, grain],
    );

    const mdaOptions: SelectProps.Option[] = useMemo(
        () =>
            (sheetsResult?.sheets ?? []).map((s) => {
                const selectable = segmentMatchesGrain(grain, s.segmentType);
                return {
                    label: s.segmentType ? `${s.sheetName} (${s.segmentType})` : s.sheetName,
                    value: s.sheetId,
                    description: s.sheetName,
                    disabled: !selectable,
                    labelTag: selectable ? undefined : 'not selectable',
                };
            }),
        [sheetsResult, grain],
    );

    const sourceOptions = isCdt ? cdtOptions : mdaOptions;
    const sourceLoading = isCdt ? tablesLoading : sheetsLoading;

    // §12.2 "ratio-attach select" as an Autosuggest. Autosuggest shows (and returns) the option's
    // `value`, so build UNIQUE display strings — source labels can collide (same table/sheet name), and
    // keying the resolution by label would silently map a pick to the wrong id. Disambiguate a duplicated
    // label with its stable id, and resolve the picked text back to its source via that display map.
    const { sourceSuggestions, sourceByDisplay } = useMemo(() => {
        const labelCounts = new Map<string, number>();
        sourceOptions.forEach((o) => labelCounts.set(String(o.label), (labelCounts.get(String(o.label)) ?? 0) + 1));
        const suggestions: AutosuggestProps.Option[] = [];
        const byDisplay = new Map<string, SelectProps.Option>();
        sourceOptions.forEach((o) => {
            const label = String(o.label);
            const display = (labelCounts.get(label) ?? 0) > 1 ? `${label} (${o.value})` : label;
            suggestions.push({ value: display, description: o.description, disabled: o.disabled, labelTag: o.labelTag });
            byDisplay.set(display, o);
        });
        return { sourceSuggestions: suggestions, sourceByDisplay: byDisplay };
    }, [sourceOptions]);

    // Resolved selection is derived from the text (keyed by the unique display string), and never a
    // not-selectable option — so buildColumns always persists the correct, id-bearing source.
    const resolved = sourceByDisplay.get(sourceText);
    const selectedSource = resolved && !resolved.disabled ? resolved : null;

    // Preselect the source when editing (the row already references a table/sheet) — set the text to that
    // source's unique display string; the derived selection + the rows effect below take it from there.
    useEffect(() => {
        if (!visible || !editingRow) return;
        const first = editingRow.cols[0];
        const value = first?.sourceRef?.cdtTableId ?? first?.sourceRef?.mdaSheetId;
        // Only seed while the text is still empty (it's reset to '' on open) so a later source-list
        // resolve or input-type change doesn't clobber a selection the user made mid-edit.
        if (value && !sourceText) {
            const entry = [...sourceByDisplay.entries()].find(([, s]) => s.value === value);
            if (entry) setSourceText(entry[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, editingKey, sourceByDisplay]);

    // When a CDT source is selected, fetch its rows so the value legend is ready on Save.
    useEffect(() => {
        if (isCdt && selectedSource?.value) {
            setRowsPayload(getCustomDataTableRowsSearchRequest(String(selectedSource.value), true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCdt, selectedSource?.value]);

    const onInputTypeChange = (option: SelectProps.Option) => {
        setInputType(option);
        setSourceText('');
        setRowsPayload(undefined);
        setValidationError(undefined);
    };

    // §12.2 inline "Create new…" link target — the module where this source type is created.
    const createNewHref = isCdt ? '/calculations/customDataTables' : '/calculations/multidimensionalAllocation';

    const handleCancel = () => cbpBuilderDispatch(ACTIONS.CLOSE_INPUT_MODAL.withPayload(undefined));

    const buildColumns = (): CommonBalancePoolColumn[] | undefined => {
        if (!selectedSource?.value) {
            setValidationError(isCdt ? 'Select a segment CDT table.' : 'Select an MDA allocation output.');
            return undefined;
        }

        if (isCdt) {
            const table = (tablesResult?.tables ?? []).find((t) => t.tableId === selectedSource.value);
            // rowsInfo.legend is typed Map<string,string> but is a plain object at runtime (JSON),
            // read by object indexing here and elsewhere (see CDTSelector) — cast via unknown.
            const legend = (rowsResult?.rowsInfo?.legend ?? {}) as unknown as Record<string, string>;
            const keys = sortedLegendKeys(legend);
            if (keys.length === 0) {
                setValidationError('This table has no value columns to add.');
                return undefined;
            }
            return keys.map((valueKey, idx) => ({
                columnId: `${selectedSource.value}:${valueKey}`,
                displayName: legend[valueKey],
                kind: 'INPUT',
                dataType: 'NUMBER',
                columnOrder: idx,
                sourceType: 'SEGMENT_CDT',
                sourceRef: {
                    cdtTableId: selectedSource.value,
                    cdtTableName: table?.tableName ?? selectedSource.description,
                    cdtColumnId: valueKey,
                },
            }));
        }

        // MDA allocation output — a single output column referencing the sheet.
        const sheet = (sheetsResult?.sheets ?? []).find((s) => s.sheetId === selectedSource.value);
        return [
            {
                columnId: `${selectedSource.value}:output`,
                displayName: `${sheet?.sheetName ?? selectedSource.description} output`,
                kind: 'INPUT',
                dataType: 'NUMBER',
                columnOrder: 0,
                sourceType: 'MDA_OUTPUT',
                sourceRef: {
                    mdaSheetId: selectedSource.value,
                    mdaSheetName: sheet?.sheetName ?? selectedSource.description,
                    mdaSheetVersion: sheet?.sheetVersion,
                },
            },
        ];
    };

    // Merge the built INPUT columns into the pool (append when adding; replace the edited source's
    // columns when editing) and persist via the shared choke point; close on success.
    const handleAdd = async () => {
        const newColumns = buildColumns();
        if (!newColumns) return;
        const existing = pool?.columns ?? [];
        // Reject a source already used by another input row — on both the add and edit paths. Duplicate
        // INPUT columnIds would collide, and persistColumns only guards dangling refs, not duplicate ids,
        // which collapses formula references (dependsOn / nameById) and React keys. On the edit path,
        // exclude the row being edited (its own source key) so re-saving the same source is allowed.
        const newSourceKey = sourceKeyOf(newColumns[0]);
        const collides = existing.some(
            (col) => col.kind === 'INPUT'
                && sourceKeyOf(col) === newSourceKey
                && sourceKeyOf(col) !== editingRow?.sourceKey,
        );
        if (collides) {
            setValidationError('This source has already been added.');
            return;
        }
        const nextColumns = editingRow
            ? [
                ...existing.filter((col) => !(col.kind === 'INPUT' && sourceKeyOf(col) === editingRow.sourceKey)),
                ...newColumns,
            ]
            : [...existing, ...newColumns];
        const ok = await persistColumns(nextColumns);
        if (ok) cbpBuilderDispatch(ACTIONS.CLOSE_INPUT_MODAL.withPayload(undefined));
    };

    // "Loading the legend" note while the CDT rows fetch is in flight.
    const legendPending = isCdt && !!selectedSource && rowsLoading;
    // Add must also wait until the loaded rows actually correspond to the current CDT source. The rows fetch
    // is driven by a post-render effect, so right after switching tables rowsPayload/rowsResult can still hold
    // the previous table while rowsLoading is momentarily false — building columns then would pair the new
    // table's id prefix with the old table's legend. Gate on the payload matching the selection AND not loading.
    const rowsReadyForSource = !!selectedSource && !rowsLoading && rowsPayload?.tableId === String(selectedSource.value);
    const addDisabled = !!saving || !selectedSource || (isCdt && !rowsReadyForSource);

    return (
        <Modal
            visible={visible}
            onDismiss={(event) => {
                event.detail.reason !== 'overlay' && handleCancel();
            }}
            header={isEditing ? 'Edit input' : 'Add input'}
            closeAriaLabel="Close modal"
            size="medium"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={handleCancel} disabled={saving}>Cancel</Button>
                        <Button variant="primary" onClick={handleAdd} loading={saving} disabled={addDisabled}>
                            {isEditing ? 'Save input' : 'Add input'}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween direction="vertical" size="m">
                <Box color="text-body-secondary">
                    Add columns to the CBP at the {grainUiLabel(grain)} grain. Columns are read from the selected
                    source; you don&apos;t enter them by hand.
                </Box>

                <FormField label="Input type">
                    <Select
                        selectedOption={inputType}
                        onChange={({ detail }) => onInputTypeChange(detail.selectedOption)}
                        options={INPUT_TYPE_OPTIONS}
                    />
                </FormField>

                <FormField
                    label={isCdt ? 'Select segment CDT table' : 'Select MDA allocation output'}
                    description={
                        isCdt
                            ? `Segment CDTs come from the CDT module. Only tables at this CBP's grain (${grainUiLabel(grain)}) are selectable.`
                            : `Only MDA outputs at this CBP's grain (${grainUiLabel(grain)}) are selectable.`
                    }
                >
                    <SpaceBetween direction="vertical" size="xs">
                        <Autosuggest
                            value={sourceText}
                            onChange={({ detail }) => setSourceText(detail.value)}
                            onSelect={({ detail }) => setSourceText(detail.value)}
                            options={sourceSuggestions}
                            filteringType="auto"
                            statusType={sourceLoading ? 'loading' : 'finished'}
                            loadingText="Loading…"
                            empty={isCdt ? 'No segment CDT tables found.' : 'No MDA outputs found.'}
                            placeholder={isCdt ? 'Choose a segment CDT table' : 'Choose an MDA allocation output'}
                            enteredTextLabel={(v) => `Use: ${v}`}
                            ariaLabel={isCdt ? 'Segment CDT table' : 'MDA allocation output'}
                        />
                        <Link href={createNewHref}>
                            {isCdt ? 'Create new segment CDT…' : 'Create new MDA allocation output…'}
                        </Link>
                    </SpaceBetween>
                </FormField>

                {legendPending && (
                    <Box color="text-body-secondary">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Spinner size="normal" />
                            <span>Reading columns from the selected table…</span>
                        </SpaceBetween>
                    </Box>
                )}

                {(validationError || sourceError || error) && (
                    <Alert
                        type="error"
                        dismissible={!!validationError}
                        header={sourceError && !validationError && !error ? 'Could not load sources' : 'Could not add input'}
                        onDismiss={() => setValidationError(undefined)}
                    >
                        {validationError || sourceError || error}
                    </Alert>
                )}
            </SpaceBetween>
        </Modal>
    );
}
